import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories, siteInfo } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function CategorySEO({ categoryName }: { categoryName: string }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - ${siteInfo.name}`,
    description: `${siteInfo.name}提供免费高清${categoryName}韩国漫画，无遮挡下拉式在线阅读，每日更新，畅享流畅浏览体验。`,
    url: `${siteInfo.url}/category/${categoryName}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
        { '@type': 'ListItem', position: 2, name: `${categoryName}漫画` },
      ],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function Category() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = categories.find((c) => c.slug === categorySlug);
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);

  useEffect(() => {
    let result = category ? comics.filter((c) => c.category === category.slug) : comics;

    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'newest') {
      result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }

    setFilteredComics(result);
  }, [categorySlug, sortBy, category]);

  const categoryName = category?.name || '全部';

  return (
    <>
      <ScrollToTop />
      <CategorySEO categoryName={categoryName} />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryName}漫画` },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部{categoryName}韩国漫画，高清无遮挡下拉式阅读
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'popular', label: '最受欢迎' },
                { key: 'newest', label: '最新更新' },
                { key: 'rating', label: '评分最高' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-6">
            <aside className="hidden lg:block w-48 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">漫画分类</h3>
                <ul className="space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          cat.slug === categorySlug
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-xs`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}